// ════════════════════════════════════════════════════════════════════════
//  Klassenblick – eine Zeile je Kind
//
//  Vorher stand hier je Lehrgang eine Tabelle mit allen Kindern, und daneben
//  noch eine für das Tempo: bei drei Lehrgängen sechs Tabellen, und ein Kind kam
//  sechsmal vor, nirgends vollständig. Die häufigste Frage ist aber nicht „wie
//  steht die Klasse in Lektion 4“, sondern „wie steht dieses Kind da“.
//
//  Sortiert wird nach dem Code, nicht nach dem Ergebnis. Eine Tabelle, die sich
//  nach Fortschritt ordnet, ist eine Bestenliste – und die will dieser Lehrgang
//  ausdrücklich nicht.
// ════════════════════════════════════════════════════════════════════════

import { Fragment } from "react";
import { Link } from "react-router-dom";
import type { OverviewRow } from "../lib/overview.js";
import { exactDate, lastPractised } from "../lib/lastPractised.js";
import { showGain, showWpm } from "../lib/speed.js";
import { ProgressBand } from "./ProgressBand.js";
import own from "./ClassOverview.module.css";
import styles from "../../../shared/components/shell.module.css";

export function ClassOverview({
  classId,
  rows,
  now = new Date(),
}: {
  classId: string;
  rows: readonly OverviewRow[];
  /** Für den Test: „gestern“ hängt vom Tag ab, an dem man hinsieht. */
  now?: Date;
}) {
  // Die Spalten sind die Lehrgänge der Klasse; jede Zeile hat dieselben.
  const courses = rows[0]?.courses ?? [];
  const withTest = courses.filter((course) => course.hasAssessment).length;

  if (courses.length === 0) {
    return (
      <p className={styles.empty}>
        Dieser Klasse ist noch kein Lehrgang zugeordnet – deshalb gibt es hier noch nichts
        zu sehen.
      </p>
    );
  }

  return (
    <>
      <div className={own.wrap}>
        <table className={styles.wide}>
          <thead>
            {/* Eine Gruppenzeile nur, wenn es etwas zu unterscheiden gibt. Bei
                einem einzigen Lehrgang wäre sie Gliederung ohne Gliederung. */}
            {courses.length > 1 && (
              <tr>
                <th />
                {courses.map((course) => (
                  <th
                    key={course.curriculumId}
                    className={own.groupHead}
                    colSpan={course.hasAssessment ? 4 : 1}
                    scope="colgroup"
                  >
                    {course.title}
                  </th>
                ))}
                <th />
              </tr>
            )}
            <tr>
              <th scope="col">Übungscode</th>
              {courses.map((course) => (
                <Fragment key={course.curriculumId}>
                  <th scope="col" className={own.groupStart}>
                    {courses.length > 1 ? "Fortschritt" : course.title}
                  </th>
                  {course.hasAssessment && (
                    <>
                      <th scope="col">Anfang</th>
                      <th scope="col">Ende</th>
                      <th scope="col">Zuwachs</th>
                    </>
                  )}
                </Fragment>
              ))}
              <th scope="col" className={`${own.quiet} ${styles.stretch}`}>
                Zuletzt geübt
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.code}>
                <td className={own.code}>
                  <Link
                    to={`/classes/${classId}/results/${row.code}`}
                    className={own.link}
                  >
                    {row.code}
                  </Link>
                </td>
                {row.courses.map((course) => {
                  const { progress } = course;
                  return (
                    <Fragment key={course.curriculumId}>
                      <td className={`${own.groupStart} ${own.band}`}>
                        <div className={own.cellInner}>
                          <ProgressBand
                            lessons={progress.lessons}
                            label={`${course.title}: ${progress.stagesDone} von ${progress.stagesTotal} Stufen geübt`}
                          />
                          <span className={own.count}>
                            {progress.stagesDone}/{progress.stagesTotal}
                          </span>
                        </div>
                      </td>
                      {course.hasAssessment && (
                        <>
                          <td className={own.number}>
                            {showWpm(progress.speed?.before ?? null)}
                          </td>
                          <td className={own.number}>
                            {showWpm(progress.speed?.after ?? null)}
                          </td>
                          <td className={own.number}>
                            {showGain(progress.speed?.gain ?? null)}
                          </td>
                        </>
                      )}
                    </Fragment>
                  );
                })}
                <td
                  className={`${own.quiet} ${styles.stretch}`}
                  title={exactDate(row.lastAt) ?? undefined}
                >
                  {lastPractised(row.lastAt, now)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {withTest > 0 && (
        <p className={styles.subtitle}>
          Anfang und Ende sind Wörter pro Minute aus dem Tempotest. Ein Strich heißt: noch
          nicht gemessen.
        </p>
      )}
    </>
  );
}
